import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  getClientCredential,
  getSelectedOrganization,
  updateGoPeopleSelectedOrganization,
  updateGoPeopleSelectedCommunity,
  getGoPeopleSelectedCommunity,
  getGoPeopleUserProfile,
  autoLoginWithTtpToken,
  getUserBlogRoleInOrganization,
} from "../api";
import {
  getAuthInfosFromCookie,
  authHasExpired,
  getCookie,
  logout,
  createAuthCookie,
  createCommunityCookie,
  getCommunityFromCookie,
  transformGoApiUser,
  buildNavCommunityFromOrg,
  transformFidToOrganization,
  extractSelectedCommunityOrgs,
  updateAuthCookieGoPeopleOrganizationId,
  setCookie,
} from "../utils";
import { APP_ENV } from "../config";
import {
  setLanguage,
  fetchFiduciary,
  setFolderFetched,
  setFiduciary,
} from "./index";
import { isJWTValid, extractAuthContextFromJWT } from "../lib/jwt";

const initialState = {
  fetching: false,
  fetched: false,
  token: "",
  jwtToken: "",
  refreshToken: "",
  goPeopleUserId: "",
  goPeopleCommunityId: "",
  goPeopleOrganizationId: "",
  clientToken: "",
  expiresIn: null,
  createdAt: null,
  user: null,
  error: null,
  loggedAs: "GUEST",
  loggedAsPost: null,
  loggedIsAdmin: false,
  navCommunity: null,
  scope: "",
  communitySwitchRequested: false,
  communityOld: null,
  autoLoging: false,
  linkedOrgsByCommunity: {},
};

// Resolves the selected community's organizations into a { [uuid]: orgs } map,
// taken straight from the selected-community response (no extra request).
const resolveSelectedCommunityOrgs = (selectedCommunityInfo, selectedCommunity) => {
  if (!selectedCommunity?.uuid) return {};
  return {
    [selectedCommunity.uuid]: extractSelectedCommunityOrgs(
      selectedCommunityInfo,
      selectedCommunity.uuid
    ),
  };
};

// The selected-organization / selected-community PUT responses may return the
// chosen org either as `selectedorganization` (old shape) or as the full
// community org list `organizations` (new shape). Resolve the one org we need:
// prefer `selectedorganization`, else the org matching `uuid`, else the first.
const resolveOrgFromSwitchResponse = (response, uuid) => {
  if (response?.selectedorganization) return response.selectedorganization;
  const orgs = response?.organizations;
  if (Array.isArray(orgs) && orgs.length > 0) {
    return (uuid && orgs.find((o) => o.uuid === uuid)) || orgs[0];
  }
  return null;
};

// Switches the selected organization within a community. The backend returns
// the chosen organization, from which we rebuild navCommunity + fiduciary.
export const updateSelectedOrganization = createAsyncThunk(
  "auth/updateSelectedOrganization",
  async ({ uuid, community }, { getState, dispatch }) => {
    try {
      const response = await updateGoPeopleSelectedOrganization(
        community,
        uuid
      );

      const org = resolveOrgFromSwitchResponse(response, uuid);
      const communityObj =
        response?.community ??
        (getState().auth.user?.communities || []).find(
          (c) => c.uuid === community
        );

      dispatch(
        setNavCommunity(buildNavCommunityFromOrg(org, community, communityObj))
      );
      if (org && org.uuid) {
        updateAuthCookieGoPeopleOrganizationId(
          null,
          org.uuid,
          org.ttp_organization_id
        );
        dispatch(setAuthGoPeopleOrganizationId(org.uuid));
        // Mark the folder unfetched first so the settings skeleton covers the
        // whole switch (the inner tabs never mount on the optimistic fiduciary).
        dispatch(setFolderFetched(false));
        // Optimistically show the new organization from the PUT response so the
        // display + ua_mode are available immediately, then refine with details.
        dispatch(setFiduciary(transformFidToOrganization(org, null, null, null)));
        // Redirect now, from the optimistic ua_mode — no need to wait for the
        // full GET /organizations response.
        dispatch(setCommunitySwitchRequested(true));
        await dispatch(fetchFiduciary(org.uuid));
      } else {
        dispatch(setCommunitySwitchRequested(true));
      }

      return {
        communityUuid: community,
        organizationUuid: org?.uuid ?? null,
        organizationApiId: org?.ttp_organization_id ?? null,
      };
    } catch (error) {
      console.error("Error updating selected organization: ", error);
      throw error;
    }
  }
);

// Switches the selected community. The backend picks a default organization
// and returns it; we rebuild navCommunity + fiduciary and flag a portal switch.
export const switchCommunity = createAsyncThunk(
  "auth/switchCommunity",
  async (communityUuid, { getState, dispatch }) => {
    try {
      const response = await updateGoPeopleSelectedCommunity(communityUuid);
      const org = resolveOrgFromSwitchResponse(response);
      const communityObj =
        response?.community ??
        (getState().auth.user?.communities || []).find(
          (c) => c.uuid === communityUuid
        );

      dispatch(
        setNavCommunity(
          buildNavCommunityFromOrg(org, communityUuid, communityObj)
        )
      );
      dispatch(setAuthGoPeopleCommunityId(communityUuid));
      if (org && org.uuid) {
        updateAuthCookieGoPeopleOrganizationId(
          null,
          org.uuid,
          org.ttp_organization_id
        );
        dispatch(setAuthGoPeopleOrganizationId(org.uuid));
        // Mark the folder unfetched first so the settings skeleton covers the
        // whole switch (the inner tabs never mount on the optimistic fiduciary).
        dispatch(setFolderFetched(false));
        // Optimistically show the new organization from the PUT response so the
        // display + ua_mode are available immediately, then refine with details.
        dispatch(setFiduciary(transformFidToOrganization(org, null, null, null)));
        // Redirect now, from the optimistic ua_mode — no need to wait for the
        // full GET /organizations response.
        dispatch(setCommunitySwitchRequested(true));
        await dispatch(fetchFiduciary(org.uuid));
      } else {
        dispatch(setCommunitySwitchRequested(true));
      }

      return {
        communityUuid,
        organizationUuid: org?.uuid ?? null,
        organizationApiId: org?.ttp_organization_id ?? null,
      };
    } catch (error) {
      console.error("Error switching community: ", error);
      throw error;
    }
  }
);

export const fetchNavCommunity = createAsyncThunk(
  "auth/fetchNavCommunity",
  async (tmp, { getState, dispatch }) => {
    const { token, navCommunity, user } = getState().auth;
    if (!token || !user) {
      return false;
    }

    return getSelectedOrganization(token).then((resp) => {
      const help = typeof resp === "string" ? JSON.parse(resp) : { ...resp };
      if (
        help &&
        help.data &&
        help.data.organization &&
        (!navCommunity || navCommunity.id !== help.data.organization.id)
      ) {
        const { organization } = help.data;
        const chosed = user.communities.filter(
          (com) => organization.id === com.id
        );

        if (!chosed || chosed.length === 0) {
          dispatch(setAuthLoggedAs("CLIENT"));
        }

        dispatch(
          setNavCommunity(
            user && user.communities && chosed && chosed.length > 0
              ? chosed[0]
              : organization
          )
        );
      }
    });
  }
);

export const handleAuthSuccess = createAsyncThunk(
  "auth/handleAuthSuccess",
  async (data, { dispatch }) => {
    if (!data.accessToken) throw new Error("No JWT token");
    if (!isJWTValid(data.accessToken)) throw new Error("Invalid JWT");

    const authC = extractAuthContextFromJWT(data.accessToken);

    const dtExpire = new Date();
    dtExpire.setTime(dtExpire.getTime() + authC.expiresAt * 1000);

    const authData = {
      jwtToken: data.accessToken,
      refreshToken: data.refreshToken,
      token: authC.ttpAccessToken,
      expiresIn: authC.expiresAt,
      createdAt: Math.floor(Date.now() / 1000),
      scope: authC.scope,
      communityUuid: data.communityId,
      goPeopleOrganizationId: data.fiduciaryId,
    };

    setCookie(`ttp_auth_${APP_ENV}`, JSON.stringify(authData), dtExpire, "/");

    const userResponse = await getGoPeopleUserProfile(data.accessToken);

    const dataRoot = userResponse.data.data[0];

    // Selected community from the dedicated endpoint (authoritative).
    const selectedCommunityInfo = await getGoPeopleSelectedCommunity().catch(
      () => null
    );

    let selectedCommunity =
      (selectedCommunityInfo &&
        dataRoot.communities.find(
          (c) =>
            c.uuid === selectedCommunityInfo.uuid ||
            (selectedCommunityInfo.ttp_community_id &&
              c.ttp_community_id === selectedCommunityInfo.ttp_community_id)
        )) ||
      dataRoot.communities.find(
        (c) => c.uuid === dataRoot.user.selected_community_id
      ) ||
      dataRoot.communities[0];

    // Organizations come embedded in the selected-community response.
    const linkedOrgsByCommunity = resolveSelectedCommunityOrgs(
      selectedCommunityInfo,
      selectedCommunity
    );
    let ttpAccessToken = authC.ttpAccessToken;

    let userBlogRole = null;
    try {
      const userSettings = await getUserBlogRoleInOrganization({
        token,
        userId: userResponse.data.data[0].user.ttp_user_id,
      });
      if (
        userSettings?.data?.data?.length > 0 &&
        userSettings.data.data[0].blogRole
      ) {
        let blogRoleInOrganization = userSettings.data.data[0].blogRole.find(
          (u) => u.organizationId === selectedCommunity?.ttp_community_id
        );
        if (blogRoleInOrganization) {
          userBlogRole = {
            ...userSettings.data.data[0],
            blogRoleInOrganization: [blogRoleInOrganization],
          };
        }
      }
    } catch (e) {
      console.warn(
        "getUserBlogRoleInOrganization failed, continue without it",
        e
      );
    }

    const userData = userResponse.data.data[0];
    setAuthUser(userData.user);

    let fiduciary = selectedCommunity ?? {};
    fiduciary["id"] = selectedCommunity?.ttp_community_id;

    let transformGoApiUserData = transformGoApiUser(
      userResponse.data.data[0],
      selectedCommunity,
      linkedOrgsByCommunity,
      userBlogRole
    );
    let dataOrganisation = transformGoApiUserData;
    let token = {
      access_token: authC.ttpAccessToken,
      createdAt: 1762535302,
      expires_in: authC.ttpExpiresIn,
      scope: authC.ttpScope,
      token_type: "bearer",
    };
    dispatch(
      setAuthTokenUser({
        token: token,
        data: dataOrganisation,
      })
    );
    dispatch(
      setNavCommunity(
        dataOrganisation && dataOrganisation.navCommunity
          ? dataOrganisation.navCommunity
          : null
      )
    );

    const authData1 = {
      jwtToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: authC.expiresAt,
      createdAt: data.createdAt,
      scope: data.scope,
      userGoApiId: dataRoot.user.uuid,
      communityGoApiId: selectedCommunity?.uuid,
      communityApiId: selectedCommunity?.ttp_community_id,
    };

    updateAuthCookieGoPeopleOrganizationId(
      data.accessToken,
      dataOrganisation?.navCommunity?.uuid,
      selectedCommunity?.uuid,
      selectedCommunity?.ttp_community_id,
      false,
      dataRoot.user.ttp_user_id
    );
    dispatch(setFolderFetched(false));
    await dispatch(fetchFiduciary());

    return {
      jwtToken: data.accessToken,
      refreshToken: data.refreshToken,
      authC,
      user: dataRoot.user,
      transform: transformGoApiUserData,
      selectedCommunity,
      linkedOrgsByCommunity,
    };
  }
);

export const fetchAuthUser = createAsyncThunk(
  "auth/fetchAuthUser",
  async (user, { getState, dispatch }) => {
    const state = getState();
    const { token, jwtToken } = getState().auth;
    let authInfos = getAuthInfosFromCookie();

    const jwtToken1 =
      authInfos?.jwtToken ||
      authInfos?.jwt ||
      user?.jwtToken ||
      user?.jwt ||
      jwtToken;

    if (jwtToken1) {
      const userResponse = await getGoPeopleUserProfile(jwtToken1);
      const dataRoot = userResponse?.data?.data?.[0];

      const selectedCommunityInfo = await getGoPeopleSelectedCommunity().catch(
        () => null
      );

      const selectedCommunity =
        (user?.fiduciaryId &&
          dataRoot?.communities?.find(
            (c) => String(c.ttp_community_id) === String(user.fiduciaryId)
          )) ||
        (selectedCommunityInfo &&
          dataRoot?.communities?.find(
            (c) =>
              c.uuid === selectedCommunityInfo.uuid ||
              (selectedCommunityInfo.ttp_community_id &&
                c.ttp_community_id === selectedCommunityInfo.ttp_community_id)
          )) ||
        dataRoot?.communities?.find(
          (c) => c.uuid === dataRoot?.user?.selected_community_id
        );

      // Organizations come embedded in the selected-community response.
      const linkedOrgsByCommunity = resolveSelectedCommunityOrgs(
        selectedCommunityInfo,
        selectedCommunity
      );

      let userBlogRole = null;

      const userSettings = await getUserBlogRoleInOrganization({
        token,
        userId: userResponse.data.data[0].user.ttp_user_id,
      });
      if (
        userSettings?.data?.data?.length > 0 &&
        userSettings.data.data[0].blogRole
      ) {
        let blogRoleInOrganization = userSettings.data.data[0].blogRole.find(
          (u) => u.organizationId === selectedCommunity?.ttp_community_id
        );
        if (blogRoleInOrganization) {
          userBlogRole = {
            ...userSettings.data.data[0],
            blogRoleInOrganization: [blogRoleInOrganization],
          };
        }
      }

      const userGoApi = transformGoApiUser(
        userResponse.data.data[0],
        selectedCommunity,
        linkedOrgsByCommunity,
        userBlogRole
      );
      // if (userResponse && userResponse.data && userResponse.data.data && userResponse.data.data[0]) {
      //   const organisationUuid = userResponse.data.data[0].selected_community.selected_organization ? userResponse.data.data[0].selected_community.selected_organization.uuid : null;
      //   const comunityUuid = userResponse.data.data[0].selected_community.uuid;
      //   // dispatch(setAuthGoPeopleOrganizationId(organisationUuid))
      //   if (comunityUuid && organisationUuid)
      //     updateAuthCookieGoPeopleOrganizationId(comunityUuid, organisationUuid);
      // }
      return userGoApi?.user;
    }

    throw new Error("Missing JWT token: cannot fetch authenticated user");
  }
);

export const autoLoginWithTtp = createAsyncThunk(
  "auth/autoLoginWithTtp",
  async (authInfos, { dispatch }) => {
    try {
      if (!authInfos?.token) return null;

      const data = await autoLoginWithTtpToken(authInfos.token);
      if (!data?.jwtToken) return null;

      const jwtToken = data.jwtToken;
      const refreshToken = data.refreshToken;

      const updatedAuth = {
        ...authInfos,
        jwtToken,
        refreshToken,
        createdAt: Math.floor(Date.now() / 1000),
      };
      const authData = {
        userApiId: updatedAuth.id,
        token: updatedAuth.token,
        jwtToken: jwtToken,
        jwt: jwtToken,
        refreshToken: refreshToken,
        expiresIn: authInfos.expiresIn,
        createdAt: authInfos.createdAt,
      };

      let dtExpire = new Date();
      dtExpire.setTime(dtExpire.getTime() + authInfos.expiresIn * 1000);

      setCookie(
        `ttp_auth_${APP_ENV}`,
        JSON.stringify(authData),
        dtExpire,
        "/",
        "unitedassociates.be"
      );
      setCookie(`ttp_auth_${APP_ENV}`, JSON.stringify(authData), dtExpire, "/");

      dispatch(setJwtToken(jwtToken));

      await dispatch(fetchAuthUser(updatedAuth)).unwrap();
      await dispatch(fetchFiduciary());

      return updatedAuth;
    } catch (error) {
      console.error("Auto login with TTP failed:", error);
      throw error;
    }
  }
);

export const fetchClientToken = createAsyncThunk(
  "auth/fetchClientToken",
  async (user, { getState, dispatch }) => {
    const response = await getClientCredential();
    const helpResp =
      typeof response === "string" ? JSON.parse(response) : response;
    return helpResp.token.access_token;
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (user, { getState, dispatch }) => {
    const state = getState();
    let authInfos = getAuthInfosFromCookie();
    if (!authInfos || authHasExpired(authInfos)) {
      logout();
    }

    const cookieLang = getCookie(`ttp_lang_${APP_ENV}`);
    if (cookieLang) {
      dispatch(setLanguage(cookieLang));
    } else {
      const lng = localStorage.getItem("lng") || "fr";
      dispatch(setLanguage(lng));
    }

    if (authInfos) {
      dispatch(setAuthToken(authInfos.token));
      // dispatch(fetchAuthUser(authInfos));
      dispatch(setExpiresIn(authInfos.expiresIn));
      dispatch(setTokenCreatedAt(authInfos.createdAt));
      dispatch(setAuthScope(authInfos.scope));
      // dispatch(fetchFiduciary());
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthTokenUser: (state, { payload }) => {
      createAuthCookie({
        token: payload.token,
        user: payload.data.user,
      });
      state.token = payload.token.access_token;
      state.expiresIn = payload.token.expiresIn;
      state.createdAt = payload.token.createdAt;
      state.user = payload.data.user;
      state.user.role = payload.data.role;
      state.scope = payload.token.scope;

      if (payload.data.organization && payload.data.organization.length > 0) {
        state.user.communities = [payload.data.organization];
        state.navCommunity = payload.data.organization;
      }

      state.loggedAs = "COLLABORATOR";
      state.loggedIsAdmin = true;
      payload.data.role && payload.data.role.typeStatus === "FOLDER"
        ? "CLIENT"
        : "COLLABORATOR";
      state.loggedAsPost =
        payload.data.role && payload.data.role.type
          ? payload.data.role.type
          : null;
      if (payload.data.role) {
        if (
          ["LEGAL_REPRESENTATIVE", "MANAGER"].includes(
            payload.data.role.type
          ) &&
          payload.data.role.typeStatus !== "FOLDER"
        ) {
          state.loggedIsAdmin = true;
        }
      }
    },
    setAuthJwtTokenUser: (state, { payload }) => {
      createAuthCookie({
        jwtToken: payload.jwtToken,
        user: payload.data.user,
      });
      state.token = payload.token.access_token;
      state.expiresIn = payload.token.expiresIn;
      state.createdAt = payload.token.createdAt;
      state.user = payload.data.user;
      state.user.role = payload.data.role;
      state.scope = payload.token.scope;

      if (payload.data.organization && payload.data.organization.length > 0) {
        state.user.communities = [payload.data.organization];
        state.navCommunity = payload.data.organization;
      }

      state.loggedAs = "COLLABORATOR";
      state.loggedIsAdmin = true;
      payload.data.role && payload.data.role.typeStatus === "FOLDER"
        ? "CLIENT"
        : "COLLABORATOR";
      state.loggedAsPost =
        payload.data.role && payload.data.role.type
          ? payload.data.role.type
          : null;
      if (payload.data.role) {
        if (
          ["LEGAL_REPRESENTATIVE", "MANAGER"].includes(
            payload.data.role.type
          ) &&
          payload.data.role.typeStatus !== "FOLDER"
        ) {
          state.loggedIsAdmin = true;
        }
      }
    },
    setAuth: (state, { payload }) => {
      state = { ...state, payload };
    },
    setAuthUser: (state, action) => {
      state.user = action.payload;
    },
    setJwtToken: (state, action) => {
      state.jwtToken = action.payload;
    },
    setAuthGoPeopleUserId: (state, action) => {
      state.goPeopleUserId = action.payload;
    },
    setAuthGoPeopleCommunityId: (state, action) => {
      state.goPeopleCommunityId = action.payload;
    },
    setAuthGoPeopleOrganizationId: (state, action) => {
      state.goPeopleOrganizationId = action.payload;
    },
    setAuthLoggedAs: (state, action) => {
      state.loggedAs = action.payload;
    },
    setAuthLoggedAsPost: (state, action) => {
      state.loggedAsPost = action.payload;
    },
    setAuthLoggedIsAdmin: (state, action) => {
      state.loggedIsAdmin = action.payload;
    },
    setExpiresIn: (state, action) => {
      state.expiresIn = action.payload;
    },
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
    },
    setTokenCreatedAt: (state, action) => {
      state.createdAt = action.payload;
    },
    setAuthToken: (state, action) => {
      state.token = action.payload;
    },
    setClientToken: (state, action) => {
      state.clientToken = action.payload;
    },
    setNavCommunity: (state, action) => {
      createCommunityCookie(action.payload);
      state.navCommunity = action.payload;
      // state.communitySwitchRequested2 = true;
      // window.location.href = "/";
    },
    setCommunitySwitchRequested: (state, action) => {
      state.communitySwitchRequested = action.payload;
    },
    setCommunityOld: (state, action) => {
      state.communityOld = action.payload;
    },
    setAutoLoging: (state, action) => {
      state.autoLoging = action.payload;
    },
    unsetNavCommunity: (state, action) => {
      state.navCommunity = null;
    },
    setAuthFetched: (state, { payload }) => {
      state.fetched = payload;
    },
    setAuthScope: (state, action) => {
      state.scope = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchClientToken.fulfilled, (state, action) => {
      state.clientToken = action.payload;
    });
    builder.addCase(fetchAuthUser.fulfilled, (state, { payload }) => {
      state.user = payload;

      // Repopulate the GoPeople community id on refresh. fetchAuthUser resolves
      // the selected community from /users/me/selected-community, but nothing
      // else writes goPeopleCommunityId on reload, so it would stay undefined
      // and every /communities/{id}/... call would hit /communities/undefined.
      if (payload.selectedCommunity?.uuid) {
        state.goPeopleCommunityId = payload.selectedCommunity.uuid;
      }

      let communityAdded = false;

      if (payload.uaRoles && payload.uaRoles.length > 0) {
        const selectedCommunityId = payload.selectedCommunity?.id;
        const selectedRole = selectedCommunityId
          ? payload.uaRoles.find((r) => r.id === selectedCommunityId)
          : null;

        const community = getCommunityFromCookie();
        const authInfos = getAuthInfosFromCookie();
        if (selectedRole) {
          state.user.role = selectedRole;
          state.navCommunity = selectedRole.organization;
          communityAdded = true;
        } else if (community || (authInfos && authInfos.communityApiId)) {
          const id = community ? community.id : authInfos.communityApiId;
          const role = payload.uaRoles.filter((r) => r.organization?.id === id);
          if (role.length > 0) {
            state.user.role = role[0];
            state.navCommunity = role[0].organization;
            communityAdded = true;
          }
        } else {
          let role = null;
          payload.uaRoles.forEach((item) => {
            if (!role || role?.organization?.uaStatus !== "VALIDATED") {
              if (!role) {
                role = item;
              } else {
                if (item.organization.uaStatus === "VALIDATED") {
                  role = item;
                } else if (
                  item.organization.uaStatus === "PENDING" &&
                  role.organization.uaStatus === "CREATED"
                ) {
                  role = item;
                }
              }
            }
          });

          state.user.role = role;
          state.navCommunity = role.organization;
          communityAdded = true;
        }
      }
      // if (!communityAdded) {
      //   if (payload.communities && payload.communities.length > 0) {
      //     if (
      //       payload.role &&
      //       payload.role.organization.id === payload.communities[0].id &&
      //       payload.role.organization.owner
      //     ) {
      //       state.navCommunity = payload.role.organization.owner;
      //     } else {
      //       if (payload.communities.length === 1) {
      //         state.navCommunity = payload.communities[0];
      //       } else {
      //         const community = payload.communities.filter(
      //           (c) => c.officeType === "ITAA"
      //         );
      //         if (community && community.length > 0) {
      //           state.navCommunity = community[0];
      //         } else {
      //           state.navCommunity = payload.communities[0];
      //         }
      //       }
      //     }
      //   }
      // }
      state.loggedAs =
        payload.role && payload.role.typeStatus === "FOLDER"
          ? "CLIENT"
          : "COLLABORATOR";
      state.loggedAsPost =
        payload.role && payload.role.type ? payload.role.type : null;
      if (payload.role) {
        if (
          ["LEGAL_REPRESENTATIVE", "MANAGER"].includes(payload.role.type) &&
          payload.role.typeStatus !== "FOLDER"
        ) {
          state.loggedIsAdmin = true;
        }
      }
      state.fetched = true;
    });
    builder.addCase(fetchAuthUser.rejected, (state, action) => {
      state.fetched = true;
    });
    builder.addCase(updateSelectedOrganization.fulfilled, (state, action) => {
      state.goPeopleCommunityId = action.payload.communityUuid;
      state.goPeopleOrganizationId = action.payload.organizationUuid;
    });
    builder.addCase(switchCommunity.fulfilled, (state, action) => {
      state.goPeopleCommunityId = action.payload.communityUuid;
      state.goPeopleOrganizationId = action.payload.organizationUuid;
    });
    builder.addCase(handleAuthSuccess.fulfilled, (state, { payload }) => {
      const {
        jwtToken,
        refreshToken,
        authC,
        user,
        transformed,
        selectedCommunity,
      } = payload;
      state.jwtToken = jwtToken;
      state.refreshToken = refreshToken;
      state.token = authC.ttpAccessToken;
      state.expiresIn = authC.expiresAt;
      state.createdAt = Math.floor(Date.now() / 1000);
      state.scope = authC.scope;

      state.goPeopleUserId = user.uuid;
      state.goPeopleCommunityId = selectedCommunity?.uuid;
      if (payload.linkedOrgsByCommunity) {
        state.linkedOrgsByCommunity = payload.linkedOrgsByCommunity;
      }
      // Seed the selected organization id immediately on login. Without this it
      // stays undefined until fetchFiduciary's reconciliation runs a beat later,
      // and org-scoped queries (collaborators PENDING/REJECTED) would fire
      // against /organizations/undefined. Defaults to the community's first org,
      // matching transformGoApiUser's selected-org convention.
      const loginSelectedOrg =
        payload.linkedOrgsByCommunity?.[selectedCommunity?.uuid]?.[0];
      if (loginSelectedOrg?.uuid) {
        state.goPeopleOrganizationId = loginSelectedOrg.uuid;
      }

      state.user = user;
      // state.navCommunity = transformed.navCommunity;
      state.loggedAs = "COLLABORATOR";
    });
  },
});

export const {
  setAuth,
  setAuthUser,
  setAuthLoggedAs,
  setAuthLoggedAsPost,
  setAuthLoggedIsAdmin,
  setAuthToken,
  setClientToken,
  setExpiresIn,
  setTokenCreatedAt,
  setNavCommunity,
  setAuthTokenUser,
  setAuthJwtTokenUser,
  setAuthFetched,
  setAuthScope,
  unsetNavCommunity,
  setJwtToken,
  setRefreshToken,
  setAuthGoPeopleUserId,
  setAuthGoPeopleCommunityId,
  setAuthGoPeopleOrganizationId,
  setCommunitySwitchRequested,
  setCommunityOld,
  setAutoLoging,
} = authSlice.actions;

export default authSlice.reducer;
